import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./api";
import { SAMPLE } from "./sampleData";

const STORE_KEY = "sharjeel_riaz_hse_client_data_v1";
const TEMPLATE_STORE_KEY = "noongravity_hse_template_data_v1";
const HSEContext = createContext(null);

const ARRAY_KEYS = [
  "incidents", "inspections", "actions", "ptws", "trainings", "observations",
  "ncrs", "audits", "waste", "legalRefs", "legalCompliance", "riskAssessments",
  "methodStatements", "documents", "equipment", "drills", "managementReviews",
  "contractorPerformance", "users", "activityLog", "notifications",
];
const EMBEDDED_FILE_KEYS = new Set(["dataUrl", "base64", "fileData", "bytes", "blob", "arrayBuffer"]);

const clone = (value) => JSON.parse(JSON.stringify(value));

export function frontendEnvironment(env = process.env) {
  const configured = String(env.REACT_APP_ENV || "").trim().toLowerCase();
  if (configured) return configured;
  return env.NODE_ENV === "production" ? "unconfigured" : "development";
}

export function sampleSeedingAllowed(environment = frontendEnvironment()) {
  return ["development", "dev", "demo", "test", "local"].includes(environment);
}

export function emptyApplicationState() {
  return {
    schemaVersion: SAMPLE.schemaVersion,
    sites: [],
    departments: [],
    months: clone(SAMPLE.months || []),
    kpiRows: clone(SAMPLE.kpiRows || []),
    settings: {
      company: "", client: "", address: "", contactPerson: "", email: "", phone: "",
      reportHeader: "", reportFooter: "", themeColor: "#082B4D", regionalLabel: "Regions / Provinces / States",
      sites: [], departments: [], regions: [], contractors: [], users: [], userRoles: [],
      inspectionTypes: [], incidentTypes: [], obsCategories: [], sorCategories: [],
      ncrCategories: [], actionOwners: [], trainingTypes: [], ptwTypes: [], auditTypes: [],
      wasteTypes: [], riskLevels: [], statusValues: [], equipmentTypes: [], documentTypes: [], drillTypes: [],
      targets: { ltif: 0, trir: 0, trainingCompliance: 0, obsClosure: 0, ncrClosure: 0, sorClosure: 0 },
    },
    monthlyKPI: {},
    monthlyKPIStatus: {},
    ...Object.fromEntries(ARRAY_KEYS.map((key) => [key, []])),
  };
}

const attachmentReference = (item) => item?.id ? {
  id: item.id,
  originalName: item.originalName || item.name || "Attachment",
  mimeType: item.mimeType || item.type || "application/octet-stream",
  storedSize: Number(item.storedSize ?? item.size) || 0,
  createdAt: item.createdAt || item.addedAt || null,
} : null;

export function stripEmbeddedFileData(value, depth = 0) {
  if (depth > 40) return null;
  if (Array.isArray(value)) return value.map((item) => stripEmbeddedFileData(item, depth + 1));
  if (!value || typeof value !== "object") return value;
  const clean = {};
  Object.entries(value).forEach(([key, child]) => {
    if (EMBEDDED_FILE_KEYS.has(key)) return;
    if (key === "attachmentsList") {
      clean[key] = Array.isArray(child) ? child.map(attachmentReference).filter(Boolean) : [];
      return;
    }
    clean[key] = stripEmbeddedFileData(child, depth + 1);
  });
  return clean;
}

// Additive compatibility only: explicit empty arrays and saved values are never replaced.
export function migrateSchema(saved, { allowSample = sampleSeedingAllowed() } = {}) {
  if (!saved || typeof saved !== "object" || Array.isArray(saved)) {
    return allowSample ? stripEmbeddedFileData(clone(SAMPLE)) : emptyApplicationState();
  }
  const baseline = allowSample ? clone(SAMPLE) : emptyApplicationState();
  const next = { ...baseline, ...saved };
  next.settings = { ...baseline.settings, ...(saved.settings || {}) };
  for (const key of ARRAY_KEYS) {
    next[key] = Array.isArray(saved[key]) ? saved[key] : (Array.isArray(baseline[key]) ? baseline[key] : []);
  }
  next.monthlyKPI = saved.monthlyKPI && typeof saved.monthlyKPI === "object" && !Array.isArray(saved.monthlyKPI)
    ? saved.monthlyKPI
    : baseline.monthlyKPI;
  next.monthlyKPIStatus = saved.monthlyKPIStatus && typeof saved.monthlyKPIStatus === "object" && !Array.isArray(saved.monthlyKPIStatus)
    ? saved.monthlyKPIStatus
    : baseline.monthlyKPIStatus;
  next.schemaVersion = Math.max(Number(saved.schemaVersion) || 0, Number(SAMPLE.schemaVersion) || 1);
  return stripEmbeddedFileData(next);
}

async function loadFromBackend() {
  try {
    const result = await apiFetch("/state");
    return { ok: true, data: result?.data || null, updatedAt: result?.updatedAt || null, error: null };
  } catch (error) {
    return { ok: false, data: null, updatedAt: null, error: error.message || "State could not be loaded." };
  }
}

async function saveToBackend(data) {
  try {
    const result = await apiFetch("/state", { method: "PUT", body: { data: stripEmbeddedFileData(data) } });
    return { ok: true, updatedAt: result?.updatedAt || null, error: null };
  } catch (error) {
    return { ok: false, updatedAt: null, error: error.message || "State could not be saved." };
  }
}

async function loadEnterpriseDashboard() {
  try {
    const result = await apiFetch("/dashboard/enterprise");
    return { ok: true, dashboard: result?.dashboard || null, error: null };
  } catch (error) {
    return { ok: false, dashboard: null, error: error.message || "Dashboard assurance data could not be loaded." };
  }
}

export function normalizeAuthoritativeSiteSummary(dashboard) {
  const raw = dashboard?.siteSummary;
  const sites = Array.isArray(raw?.sites) ? raw.sites : [];
  return {
    totalSites: Number(raw?.totalSites) || 0,
    activeSites: Number(raw?.activeSites) || 0,
    filteredSites: Number(raw?.filteredSites) || sites.length,
    sites,
  };
}

export function filterAuthoritativeSiteSummary(summary, filters = {}) {
  const allSites = Array.isArray(summary?.sites) ? summary.sites : [];
  const sites = allSites.filter((site) => {
    if (filters.site && filters.site !== "All Sites" && site.name !== filters.site && site.code !== filters.site) return false;
    if (filters.region && filters.region !== "All Regions" && site.regionName !== filters.region) return false;
    return true;
  });
  return { ...summary, filteredSites: sites.length, sites };
}

export function HSEProvider({ children }) {
  const environment = frontendEnvironment();
  const allowSample = sampleSeedingAllowed(environment);
  const initialState = useMemo(() => {
    if (allowSample) {
      try {
        const raw = localStorage.getItem(STORE_KEY) || localStorage.getItem(TEMPLATE_STORE_KEY);
        if (raw) return migrateSchema(JSON.parse(raw), { allowSample });
      } catch {}
    }
    return migrateSchema(null, { allowSample });
  }, [allowSample]);
  const [data, setData] = useState(initialState);
  const [filters, setFilters] = useState({
    month: "All",
    year: String(new Date().getFullYear()),
    site: "All Sites",
    department: "All Departments",
    region: "All Regions",
    search: "",
  });
  const [lastRefreshed, setLastRefreshed] = useState(new Date().toISOString());
  const [syncStatus, setSyncStatus] = useState("loading");
  const [syncError, setSyncError] = useState("");
  const [enterpriseDashboard, setEnterpriseDashboard] = useState(null);
  const [enterpriseDashboardStatus, setEnterpriseDashboardStatus] = useState("loading");
  const saveTimer = useRef(null);
  const initialLoading = useRef(true);
  const lastPersisted = useRef(null);
  const initialData = useRef(initialState);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [remote, enterprise] = await Promise.all([loadFromBackend(), loadEnterpriseDashboard()]);
      if (cancelled) return;
      setEnterpriseDashboard(enterprise.dashboard);
      setEnterpriseDashboardStatus(enterprise.ok ? "ready" : "error");
      if (remote.ok && remote.data) {
        const migrated = migrateSchema(remote.data, { allowSample });
        lastPersisted.current = JSON.stringify(stripEmbeddedFileData(migrated));
        setData(migrated);
        setSyncStatus("synced");
      } else if (remote.ok && allowSample) {
        const saved = await saveToBackend(initialData.current);
        if (cancelled) return;
        lastPersisted.current = saved.ok ? JSON.stringify(stripEmbeddedFileData(initialData.current)) : null;
        setSyncStatus(saved.ok ? "synced" : "offline");
        setSyncError(saved.error || "");
      } else if (remote.ok) {
        const empty = emptyApplicationState();
        lastPersisted.current = JSON.stringify(empty);
        setData(empty);
        setSyncStatus("synced");
      } else {
        lastPersisted.current = JSON.stringify(stripEmbeddedFileData(initialData.current));
        setSyncStatus("offline");
        setSyncError(remote.error || "State could not be loaded.");
      }
      initialLoading.current = false;
    })();
    return () => { cancelled = true; };
  }, [allowSample]);

  useEffect(() => {
    if (!allowSample) return;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(stripEmbeddedFileData(data)));
      localStorage.removeItem(TEMPLATE_STORE_KEY);
    } catch {}
  }, [allowSample, data]);

  useEffect(() => {
    if (initialLoading.current) return undefined;
    const serialized = JSON.stringify(stripEmbeddedFileData(data));
    if (serialized === lastPersisted.current) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSyncStatus("saving");
    saveTimer.current = setTimeout(async () => {
      const result = await saveToBackend(data);
      if (result.ok) lastPersisted.current = serialized;
      setSyncStatus(result.ok ? "synced" : "offline");
      setSyncError(result.error || "");
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [data]);

  const update = useCallback((patch) => setData((current) => ({ ...current, ...patch })), []);
  const resetSample = useCallback(async () => {
    if (!allowSample) return { ok: false, error: "Sample reset is disabled outside development, demo and test environments." };
    setData(migrateSchema(null, { allowSample: true }));
    setLastRefreshed(new Date().toISOString());
    return { ok: true };
  }, [allowSample]);
  const refresh = useCallback(async () => {
    setSyncStatus("loading");
    setEnterpriseDashboardStatus("loading");
    const [remote, enterprise] = await Promise.all([loadFromBackend(), loadEnterpriseDashboard()]);
    if (remote.ok) {
      const next = remote.data ? migrateSchema(remote.data, { allowSample }) : migrateSchema(null, { allowSample });
      lastPersisted.current = JSON.stringify(stripEmbeddedFileData(next));
      setData(next);
    }
    setLastRefreshed(new Date().toISOString());
    setSyncStatus(remote.ok ? "synced" : "offline");
    setSyncError(remote.error || "");
    setEnterpriseDashboard(enterprise.dashboard);
    setEnterpriseDashboardStatus(enterprise.ok ? "ready" : "error");
    return remote.ok && enterprise.ok;
  }, [allowSample]);
  const persistData = useCallback(async (nextData) => {
    const sanitized = stripEmbeddedFileData(nextData);
    const serialized = JSON.stringify(sanitized);
    setSyncStatus("saving");
    setSyncError("");
    const result = await saveToBackend(sanitized);
    if (!result.ok) {
      setSyncStatus("offline");
      setSyncError(result.error || "State could not be saved.");
      throw new Error(result.error || "State could not be saved.");
    }
    lastPersisted.current = serialized;
    setData(sanitized);
    setLastRefreshed(new Date().toISOString());
    setSyncStatus("synced");
    return result;
  }, []);

  const siteSummary = useMemo(() => normalizeAuthoritativeSiteSummary(enterpriseDashboard), [enterpriseDashboard]);
  const filteredSiteSummary = useMemo(() => filterAuthoritativeSiteSummary(siteSummary, filters), [siteSummary, filters]);
  const siteNames = useMemo(() => {
    if (enterpriseDashboard) return siteSummary.sites.map((site) => site.name || site.code).filter(Boolean);
    const sites = data.settings?.sites || [];
    return sites.length && typeof sites[0] === "object" ? sites.map((site) => site.number || site.name) : sites;
  }, [data.settings?.sites, enterpriseDashboard, siteSummary.sites]);
  const activeSites = useMemo(() => {
    if (enterpriseDashboard) return siteSummary.sites.filter((site) => site.status === "active").map((site) => site.name || site.code).filter(Boolean);
    const sites = data.settings?.sites || [];
    return sites.length && typeof sites[0] === "object"
      ? sites.filter((site) => site.status === "Active" || site.status === "active").map((site) => site.number || site.name)
      : sites;
  }, [data.settings?.sites, enterpriseDashboard, siteSummary.sites]);
  const value = useMemo(() => ({
    data, setData, update, persistData, filters, setFilters, lastRefreshed, refresh, resetSample,
    siteNames, activeSites, siteSummary, filteredSiteSummary, enterpriseDashboard, enterpriseDashboardStatus,
    syncStatus, syncError, environment, sampleSeedingEnabled: allowSample,
  }), [data, update, persistData, filters, lastRefreshed, refresh, resetSample, siteNames, activeSites, siteSummary, filteredSiteSummary, enterpriseDashboard, enterpriseDashboardStatus, syncStatus, syncError, environment, allowSample]);
  return <HSEContext.Provider value={value}>{children}</HSEContext.Provider>;
}

export function useHSE() {
  const context = useContext(HSEContext);
  if (!context) throw new Error("useHSE must be used inside HSEProvider");
  return context;
}

export function applyFilter(rows, filters, dateField = "date") {
  return rows.filter((record) => {
    if (filters.site !== "All Sites" && record.site && record.site !== filters.site) return false;
    if (filters.department !== "All Departments" && record.department && record.department !== filters.department) return false;
    if (filters.month !== "All" && record[dateField] && new Date(record[dateField]).getMonth() !== Number(filters.month)) return false;
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!Object.values(record).some((value) => String(value).toLowerCase().includes(query))) return false;
    }
    return true;
  });
}
